import { useQuery } from '@tanstack/react-query'
import { fetchCustomEndpointOnce } from './customEndpointFetch'

// Auth semantics (cookie-or-token, quiet retry-once on 401, and the
// 2026-07-13 browser-reopen regression they encode) live in
// customEndpointFetch so they cannot drift between custom-endpoint hooks.
//
// This hook's own contract on top of that is "fall back to showing all
// devices": we deliberately do NOT clear the session token on auth failure,
// so a 401 just degrades gracefully without contributing to the device-click
// logout race the dataProvider already had to harden against.
const fetchDevicesWithMetricsOnce = (signal?: AbortSignal) =>
  fetchCustomEndpointOnce('/devices-with-metrics', signal)

/**
 * Fetches the set of device IDs that have at least one metric record.
 * Used to filter out devices/pets with no data from the UI.
 *
 * Returns null while loading or on error (callers should show all devices as fallback).
 *
 * Backed by react-query with a stable key so the result is fetched ONCE and
 * shared: PetShow and its nested EnhancedDeviceDatagrid both call this hook, and
 * every pet navigation re-mounts them — previously that meant 2+ uncached calls
 * per page, each an expensive backend scan for admin roles
 * (`SELECT DISTINCT device_id FROM metrics` — ~0.5s on the prod metrics table).
 * react-query dedups concurrent callers and serves the cached membership set
 * within `staleTime`, so the render gates that depend on this (PetShow's
 * settled-gate, the datagrid's loading guard) resolve instantly on cache hits.
 *
 * Degraded results (no token / auth failure / non-2xx / malformed) THROW rather
 * than resolving to null, so react-query does NOT cache the "show all" fallback:
 * an errored query stays stale and refetches on the next mount once auth settles,
 * instead of pinning show-all for `staleTime`. The caller still degrades to
 * show-all via `data ?? null` for the duration of the failure. The 401-retry-once
 * and no-token-skip semantics live inside fetchDevicesWithMetricsOnce, unchanged.
 */
export const useDevicesWithMetrics = (): {
  devicesWithMetrics: Set<number> | null
  isLoading: boolean
} => {
  const { data, isPending } = useQuery<Set<number>>({
    queryKey: ['devices-with-metrics'],
    queryFn: async ({ signal }) => {
      const res = await fetchDevicesWithMetricsOnce(signal)
      if (!res || !res.ok) {
        // No token / auth failure / any other non-2xx — throw so this degraded
        // state is not cached as a success; caller degrades to show-all.
        throw new Error('devices-with-metrics unavailable')
      }
      const body = await res.json()
      if (!body?.deviceIds || !Array.isArray(body.deviceIds)) {
        throw new Error('devices-with-metrics malformed response')
      }
      return new Set<number>(body.deviceIds)
    },
    // The fetcher already does the quiet 401-retry-once; don't stack
    // react-query retries (would multiply calls on genuine errors).
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })

  return { devicesWithMetrics: data ?? null, isLoading: isPending }
}

/**
 * Helper: checks if a device has metrics data.
 * Returns true if the metrics check failed (fallback to showing the device).
 */
export const deviceHasMetrics = (
  deviceId: number,
  devicesWithMetrics: Set<number> | null,
): boolean => {
  if (devicesWithMetrics === null) return true // Fallback: show all
  return devicesWithMetrics.has(deviceId)
}
